import { ResponseError } from '@backstage/errors';
import type { TechRadarLoaderResponse } from '@backstage-community/plugin-tech-radar-common';
import { techRadarDataSchema } from '@ns/plugin-ns-tech-radar-common';
import { useAsyncResolver } from '@ns/plugin-react-async-utils';

/**
 * Result shape returned by {@link useLiveRadarData}.
 *
 * Mirrors the `react-use` `useAsync` contract intentionally so the consuming
 * component can switch on a single discriminator (`loading` first, then
 * `error`, then `data`) without having to track an extra status enum.
 *
 * This is the stable outside contract of the hook. The entity overview and
 * detail content depend on exactly these three fields; the migration to the
 * shared {@link useAsyncResolver} primitive keeps them unchanged and only
 * moves the fetch lifecycle into the primitive.
 */
export interface UseLiveRadarDataResult {
  data: TechRadarLoaderResponse | undefined;
  error: Error | undefined;
  loading: boolean;
}

/**
 * The one async operation this hook runs: fetch the TechRadar JSON document and
 * validate it against the shared {@link techRadarDataSchema}. It receives the
 * {@link AbortSignal} from the primitive so an in-flight request is cancelled on
 * unmount or when the URL changes.
 */
async function fetchRadarDocument(
  dataFile: string,
  signal: AbortSignal,
): Promise<TechRadarLoaderResponse> {
  // eslint-disable-next-line ns/no-platform-fetch -- g55l.22.4 preserves ResponseError's response contract during migration.
  const response = await fetch(dataFile, { signal });
  if (!response.ok) {
    // ResponseError renders nicely in ResponseErrorPanel and carries the HTTP
    // status the catalog operator needs to diagnose a 404.
    throw await ResponseError.fromResponse(response);
  }
  const json: unknown = await response.json();
  const parsed = techRadarDataSchema.safeParse(json);
  if (!parsed.success) {
    throw new Error(
      `TechRadar live data failed schema validation: ${parsed.error.message}`,
    );
  }
  // The community parser coerces `date` strings to `Date` objects via
  // `z.coerce.date()`, so the parsed result is already shaped like
  // `TechRadarLoaderResponse` and no extra transform is required.
  return parsed.data;
}

/**
 * Fetch a TechRadar JSON document at render time, validate it against the
 * shared {@link techRadarDataSchema} and return it in the same shape the
 * cached path produces via {@link transformRadarData}.
 *
 * Used by the entity overview content and detail page when the catalog
 * entity carries `spec.fetchMode === 'live'`. The processor never stores
 * the radar payload in that mode, so the frontend must fetch it directly
 * from the URL declared in `spec.dataFile`.
 *
 * Implemented as a thin wrapper around the shared {@link useAsyncResolver}
 * primitive: the primitive owns loading, cancellation and stale-response
 * suppression, while this hook supplies the fetch-and-validate operation and
 * maps the primitive's discriminated-union state back onto the stable
 * `{ data, error, loading }` contract its consumers expect.
 *
 * @param dataFile - The URL to fetch. When `undefined`, the hook resolves
 *   immediately with an error so callers can render a panel without a
 *   network round-trip.
 */
export function useLiveRadarData(
  dataFile: string | undefined,
): UseLiveRadarDataResult {
  const state = useAsyncResolver<
    { dataFile: string | undefined },
    TechRadarLoaderResponse
  >(
    async (ctx, signal) => {
      if (!ctx.dataFile) {
        throw new Error(
          'Live-mode TechRadar requires spec.dataFile to be set.',
        );
      }
      return fetchRadarDocument(ctx.dataFile, signal);
    },
    { dataFile },
    { cacheKey: ctx => ctx.dataFile ?? '' },
  );

  switch (state.status) {
    case 'success':
      return { data: state.data, error: undefined, loading: false };
    case 'error':
      return { data: undefined, error: state.error, loading: false };
    default:
      // 'idle' and 'loading' both present as loading to the consumer, matching
      // the previous behaviour where the hook started in a loading state.
      return { data: undefined, error: undefined, loading: true };
  }
}
